import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, AlertTriangle, Loader2, CreditCard } from 'lucide-react';

/**
 * Tiny green/red pill polling the Inspyre/NMI gateway. Sits in the admin
 * Dashboard header so admins notice a payment outage BEFORE a customer
 * reports it. Hover for the latency + last-checked timestamp; click to
 * force a refetch.
 */
const POLL_MS = 60_000;

const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' });
  } catch (_) { return iso; }
};

const GatewayHealthIndicator = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);

  const probe = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/inspyre/health');
      setHealth(data);
    } catch (e) {
      setHealth({
        ok: false,
        latency_ms: 0,
        checked_at: new Date().toISOString(),
        message: e.response?.data?.detail || e.message || 'Probe failed',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    probe();
    const id = setInterval(probe, POLL_MS);
    return () => clearInterval(id);
  }, [probe]);

  const ok = health?.ok;
  const dotColor = loading ? '#94a3b8' : ok ? '#16a34a' : '#dc2626';
  const bg = loading ? '#f1f5f9' : ok ? '#dcfce7' : '#fee2e2';
  const fg = loading ? '#475569' : ok ? '#166534' : '#991b1b';
  const label = loading
    ? 'Checking gateway…'
    : ok
      ? `Gateway OK · ${health?.latency_ms ?? '–'}ms`
      : 'Gateway issue';

  const title = health
    ? `${health.message}\nLatency: ${health.latency_ms}ms\nLast checked: ${fmtTime(health.checked_at)}\nEndpoint: ${health.api_url || '?'}`
    : 'Polling Inspyre / NMI gateway…';

  return (
    <button
      type="button"
      onClick={probe}
      data-testid="gateway-health-indicator"
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        borderRadius: 999,
        background: bg,
        border: `1px solid ${ok ? '#bbf7d0' : ok === false ? '#fecaca' : '#cbd5e1'}`,
        color: fg,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.3,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      <CreditCard size={12} />
      {loading ? (
        <Loader2 size={12} className="animate-spin" />
      ) : ok ? (
        <CheckCircle2 size={12} />
      ) : (
        <AlertTriangle size={12} />
      )}
      <span data-testid="gateway-health-label">{label}</span>
      <span
        style={{
          width: 8, height: 8, borderRadius: '50%', background: dotColor,
          boxShadow: ok ? '0 0 6px rgba(22,163,74,0.5)' : 'none',
        }}
      />
    </button>
  );
};

export default GatewayHealthIndicator;
